import ExperienceHeader from '../Form/Partials/ExperienceHeader';
import ExperienceLayout from '../Form/Partials/ExperienceLayout';
import { useCallback, useLayoutEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { Form, Formik, useFormikContext } from 'formik';
import { put } from '../../../api/node';
import { useApp, useSearchParams, useSnack } from '../../../hooks';
import { Stack } from '@koob/margaret';
import {
  KoobButton,
  KoobLoader,
  KoobPlaceholder,
  KoobSkeleton,
} from '@koob/ui';
import { Container } from 'ui';
import { isEqual } from 'lodash';
import LocationAwareSearch from 'components/LocationAwareSearch';
import LocalMultiSearchableSelectField from 'components/Fields/LocalMultiSearchableSelectField';
import {
  KoobLocationSearch,
  SEARCH_DEFAULT_TYPES,
  SEARCH_TYPES,
} from '@koob/search';
import { getExperiences, getExperience } from 'api/node/experiences';
import { buildExtrasPayload } from './buildExtrasPayload';
import { ExperienceTypeEnum } from '@koob/enums';

const LOCATION_SEARCH_TYPES = [...SEARCH_DEFAULT_TYPES, SEARCH_TYPES.PROGRAM];

function ExperienceOption({ index, recommandableExperienceList, extraId }) {
  const { t } = useTranslation('experiences');

  const options = (recommandableExperienceList ?? []).reduce(
    (acc, experience) => {
      if (experience?.id !== extraId) {
        acc.push({
          label: experience.name,
          value: experience.id,
        });
      }
      return acc;
    },
    [],
  );

  return (
    <Stack
      direction="column"
      gutterSize={1.5}
      size="full"
      className="p-4 border rounded-lg"
    >
      <Stack size="full">
        <LocalMultiSearchableSelectField
          label={t('extraRecommendation')}
          name={`extras.${index}.recommendations`}
          options={options}
          renderOption={(option) => option.label}
          renderSelectedOption={(option) => option.label}
        />
      </Stack>
    </Stack>
  );
}

// Appends rows for newly-matched search results without touching rows
// already in Formik state — a full reinitialize on every search keystroke
// would discard unsaved edits on rows the search happens to hide.
function SyncRecommendedRows({ formatDatas }) {
  const { setValues } = useFormikContext();

  useLayoutEffect(() => {
    setValues((current) => {
      const missingRows = (formatDatas ?? []).filter(
        (row) => !current.extras?.some((existing) => existing.id === row.id),
      );

      if (missingRows.length === 0) return current;

      return {
        ...current,
        extras: [...(current.extras ?? []), ...missingRows],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatDatas]);

  return null;
}

function ExperienceSearchItem({ experience, isBestMatch, children }) {
  const { t } = useTranslation('experiences');

  return (
    <div
      className={['bg-gray-100 border rounded-md py-1 px-2 w-full'].join(' ')}
    >
      <div className="flex justify-between items-start space-x-2 w-full">
        <div className="text-sm text-gray-500">
          <div className="flex items-center font-medium gap-2 z-1">
            {experience.name}{' '}
            {isBestMatch && <i className="fa-regular fa-medal" />}
          </div>
          <div className="text-xs">
            {experience?.inCity
              ? `${experience?.inCity?.title}, ${experience?.inCity?.country?.title}`
              : t('steps.compatibility.search.noLocation')}
          </div>
          <div className="flex mt-2">
            <div className="flex gap-0.5 items-center">
              {experience?.startPosition?.name && (
                <i className="far fa-flag"></i>
              )}
              <p className="text-textLighter text-bodySmall">
                {' '}
                {experience?.startPosition?.name}{' '}
              </p>
            </div>
            <div className="flex gap-0.5 items-center ml-3">
              {experience?.endPosition?.name && (
                <i className="far fa-flag-checkered"></i>
              )}
              <p className="text-textLighter text-bodySmall">
                {' '}
                {experience?.endPosition?.name}{' '}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

export function ExtraRecommendation() {
  const { t, i18n } = useTranslation('experiences');
  const { notify, notifyError } = useSnack();
  const { experienceId } = useParams();
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchParams] = useSearchParams();
  const { currentOrganizationId } = useApp();

  const {
    data: experienceData,
    isLoading,
    reload,
  } = useAsync({
    promiseFn: getExperience,
    experienceId: experienceId,
  });

  const { data, isLoading: isProgramsLoading } = useAsync({
    promiseFn: getExperiences,
    filters: {
      excludedExperiences: [experienceId],
      search:
        filters?.location?.kind === SEARCH_TYPES.EXPERIENCE
          ? filters?.location?.title
          : undefined,
      cities:
        filters?.location?.kind === SEARCH_TYPES.CITY
          ? [filters?.location?.id]
          : [],
      countries:
        filters?.location?.kind === SEARCH_TYPES.COUNTRY
          ? [filters?.location?.id]
          : [],
      startingPoint: searchParams?.startingPoint,
      endingPoint: searchParams?.endingPoint,
      types: [ExperienceTypeEnum.PROGRAM],
      skipImage: true,
    },
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  const results = data?.data || [];
  const updateFilterValue = useCallback(
    (property, value) => {
      setFilters((current) => ({
        ...current,
        [property]: value,
      }));
    },
    [setFilters],
  );

  const experience = experienceData?.data;

  const recommendedExperiences = results
    .filter((item) => {
      return !experienceData?.data?.incompatibilities.some(
        (incompatItem) => incompatItem.id === item.id,
      );
    })
    .sort((a, b) => {
      const aBestIndex = experience?.bestMatches.findIndex(
        (item) => item.id === a.id,
      );
      const bBestIndex = experience?.bestMatches.findIndex(
        (item) => item.id === b.id,
      );

      if (aBestIndex !== -1 && bBestIndex === -1) {
        return -1;
      } else if (aBestIndex === -1 && bBestIndex !== -1) {
        return 1;
      } else {
        return 0;
      }
    });
  const incluedIds = [];
  experience?.programs?.forEach((item) => {
    item.includedExperiences.forEach((exp) => {
      incluedIds.push(exp.id);
    });
  });
  const optionsRecommandables = experience?.extras?.filter(
    (item) => !incluedIds?.includes(item?.id),
  );

  function groupExtrasByRecommendations(extrasList) {
    if (!extrasList?.length) return [];

    const recommendationsMap = new Map();

    for (const extra of extrasList) {
      for (const recommendation of extra.recommendations) {
        if (!recommendationsMap.has(recommendation.id)) {
          recommendationsMap.set(recommendation.id, {
            ...recommendation,
            recommendations: [],
          });
        }

        recommendationsMap.get(recommendation.id).recommendations.push({
          value: extra.id,
          label: extra.name,
          cancellationPolicies: extra.cancellationPolicies,
        });
      }
    }

    return Array.from(recommendationsMap.values());
  }

  const save = async (values) => {
    try {
      setSaving(true);
      // Re-read the experience right before building the payload: the merge
      // baseline must be the current server state, not the snapshot from page
      // load — a colleague may have saved requirements on other programs since.
      const freshExperience =
        (await getExperience({ experienceId }))?.data ?? experience;

      // `values.extras` = every row the session has surfaced (accumulated
      // across searches by SyncRecommendedRows). The payload is the session's
      // edits merged over the server state: pairs of programs this session
      // never loaded are preserved instead of being wiped to [] (KD-3626).
      const extras = buildExtrasPayload(
        freshExperience?.extras,
        values.extras,
        (freshExperience?.incompatibilities ?? []).map((item) => item.id),
      );

      const programs = freshExperience.programs.map((program) => {
        return {
          ...program,
          startingTime: program.startingTime?.slice(0, 5),
          endingTime: program.endingTime?.slice(0, 5),
        };
      });

      await put(`/experiences/${experienceId}`, {
        ...freshExperience,
        programs,
        extras,
      });
      setSaving(false);
      reload();
      notify(t('editSuccess'));
      return experienceId;
    } catch (err) {
      setSaving(false);
      if (err?.response?.data?.message) {
        notify(err?.response?.data?.message, { type: 'error' });
      } else {
        notifyError(err);
      }
    }
  };

  const groupedExtras = groupExtrasByRecommendations(experience?.extras);
  const formatDatas = recommendedExperiences?.map((item) => {
    const extra = groupedExtras?.find((extra) => extra.id === item?.id);
    return {
      id: item?.id,
      name: item?.name,
      recommendations: extra?.recommendations,
    };
  });
  const excludedExperienceIds = experienceData?.data?.incompatibilities?.map(
    (item) => item.id,
  );

  return (
    <>
      <ExperienceHeader experience={experienceData?.data} />
      <ExperienceLayout>
        <div className="relative">
          {saving && (
            <div className="bg-gray-100/75 backdrop-blur-sm rounded-lg border z-20 absolute h-full w-full flex justify-center items-center">
              <KoobLoader centered />
            </div>
          )}

          {!isLoading ? (
            <>
              <Formik
                initialValues={{
                  extras: formatDatas,
                }}
                onSubmit={save}
              >
                {({ values, submitForm }) => (
                  <Form>
                    <SyncRecommendedRows formatDatas={formatDatas} />
                    <div className="mb-5">
                      <div className="bg-white border rounded-md p-5">
                        <div className="flex justify-center space-x-3">
                          <KoobButton variant="primary" onClick={submitForm}>
                            {t('misc:save')}
                          </KoobButton>

                          <Link to={`/experiences/${experienceId}`}>
                            <KoobButton color="secondary">
                              {t('misc:cancel')}
                            </KoobButton>
                          </Link>
                        </div>
                      </div>
                    </div>

                    <Container size="full">
                      <div className="flex flex-row justify-between mb-10 gap-4">
                        <div className="my-3 flex flex-col space-y-4 w-3/5">
                          <KoobLocationSearch
                            placeholder={t('locationsSearch', {
                              ns: 'experiences',
                            })}
                            types={LOCATION_SEARCH_TYPES}
                            locale={i18n.language}
                            fromOrganizationId={
                              experienceData?.data?.organizationId ??
                              currentOrganizationId
                            }
                            value={filters?.location}
                            onValueChange={(value) =>
                              updateFilterValue('location', value)
                            }
                            excludedExperienceIds={excludedExperienceIds}
                            kinds={[
                              SEARCH_TYPES.COUNTRY,
                              SEARCH_TYPES.CITY,
                              SEARCH_TYPES.EXPERIENCE,
                            ]}
                          />
                        </div>
                        <div className="my-3 flex flex-col space-y-3">
                          <LocationAwareSearch
                            name="startingPoint"
                            placeholder={t('startingPoint')}
                          />
                        </div>

                        <div className="my-3 flex flex-col space-y-3">
                          <LocationAwareSearch
                            name="endingPoint"
                            placeholder={t('endingPoint')}
                          />
                        </div>
                      </div>

                      {isProgramsLoading && (
                        <div className="flex justify-center py-4">
                          <KoobSkeleton shape="rows" />
                        </div>
                      )}

                      <Stack direction="column" gutterSize={1.5} size="full">
                        {(recommendedExperiences || []).map(
                          (program, index) => (
                            <ExperienceSearchItem
                              key={index}
                              experience={program}
                              isBestMatch={Boolean(
                                experience?.bestMatches.some(
                                  (item) => item.id === program.id,
                                ),
                              )}
                            >
                              <ExperienceOption
                                recommandableExperienceList={
                                  optionsRecommandables
                                }
                                experience={program}
                                index={values.extras.findIndex(
                                  (experience) => experience.id === program.id,
                                )}
                                extraId={program?.id}
                              />
                            </ExperienceSearchItem>
                          ),
                        )}
                      </Stack>
                    </Container>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            <>
              <div className="mb-5">
                <KoobPlaceholder className="rounded-lg h-20 w-full" />
              </div>

              <div className="grid grid-cols-1 gap-3">
                <KoobPlaceholder className="rounded-lg h-14 mt-2 w-full" />
                <KoobPlaceholder className="rounded-lg h-14 mt-2 w-full" />
                <KoobPlaceholder className="rounded-lg h-14 mt-2 w-full" />
              </div>
            </>
          )}
        </div>
      </ExperienceLayout>
    </>
  );
}
